// src/components/Layout/Header.jsx
import { useMemo, useState, useRef, useEffect, memo } from 'react';
import { Bell, User, Menu, ChevronDown, Users, ShoppingBag, MessageCircle, Settings, LogOut, Zap, BarChart3, FileText, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth'; // Assuming this provides logout function

export const Header = memo(({ onMenuToggle }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const profileRef = useRef(null);
  const { user, logout } = useAuth(); // Assumes useAuth exposes user and logout

  // Memoize icons for perf
  const rightIcons = useMemo(
    () => (
      <>
        <button className="p-2 text-gray-500 hover:text-gray-700 transition-colors" aria-label="Notifications">
          <Bell className="h-5 w-5" />
        </button>
        <button
          ref={profileRef}
          className="relative p-2 text-gray-500 hover:text-gray-700 transition-colors flex items-center space-x-1"
          onClick={() => setIsProfileOpen(!isProfileOpen)}
          aria-label="Profile"
        >
          <User className="h-5 w-5" />
          {user && <span className="text-xs font-medium text-gray-700 hidden sm:inline">{user.name || user.email}</span>}
          <ChevronDown className={`h-4 w-4 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
        </button>
      </>
    ),
    [user, isProfileOpen]
  );

  // Detect mobile for responsive dropdown behavior
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setIsProfileOpen(false);
      }
    };
    if (isProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isProfileOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsProfileOpen(false);
    };
    if (isProfileOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isProfileOpen]);

  // Admin features for AI-Powered Hybrid Digital Marketplace + Community
  const adminFeatures = [
    {
      icon: BarChart3,
      label: 'Dashboard',
      path: '/admin/dashboard',
      description: 'Overview of marketplace metrics and community activity'
    },
    {
      icon: Users,
      label: 'User Management',
      path: '/admin/users',
      description: 'Manage users, roles, and permissions'
    },
    {
      icon: ShoppingBag,
      label: 'Products & Inventory',
      path: '/admin/products',
      description: 'Handle listings, stock, and hybrid sales (digital/physical)'
    },
    {
      icon: Zap,
      label: 'AI Tools',
      path: '/admin/ai-tools',
      description: 'Configure AI recommendations, chatbots, and analytics'
    },
    {
      icon: MessageCircle,
      label: 'Community Management',
      path: '/admin/community',
      description: 'Moderate forums, posts, and engagement'
    },
    {
      icon: FileText,
      label: 'Orders & Transactions',
      path: '/admin/orders',
      description: 'Track sales, refunds, and blockchain integrations'
    },
    {
      icon: Shield,
      label: 'Security & Compliance',
      path: '/admin/security',
      description: 'Audit logs, fraud detection, and policy enforcement'
    },
    {
      icon: Settings,
      label: 'Settings',
      path: '/admin/settings',
      description: 'App configuration and integrations'
    }
  ];

  return (
    <header className="bg-white shadow-sm border-b px-4 sm:px-6 py-4 flex justify-between items-center sticky top-0 z-30">
      {/* Left side: Title and mobile menu button grouped together */}
      <div className="flex items-center space-x-4">
        <button
          className="p-2 text-gray-500 hover:text-gray-700 transition-colors md:hidden"
          onClick={onMenuToggle}
          aria-label="Toggle menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <h1 className="text-xl font-semibold text-gray-900 whitespace-nowrap">Super Admin</h1>
      </div>

      {/* Right side: Icons */}
      <div className="flex items-center space-x-2 sm:space-x-4 relative">
        {rightIcons}

        {/* Profile Dropdown */}
        {isProfileOpen && (
          <div
            className={`absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-40 transition-all duration-200 ease-in-out transform ${
              isProfileOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            } ${isMobile ? 'w-full right-auto left-0 max-h-96 overflow-y-auto' : ''}`}
          >
            {/* User Info Header */}
            {user && (
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{user.name || user.email}</p>
                    <p className="text-xs text-gray-500">{user.role || 'Admin'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Features List */}
            <div className="py-1">
              {adminFeatures.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Link
                    key={feature.path}
                    to={feature.path}
                    onClick={() => setIsProfileOpen(false)}
                    className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors w-full"
                  >
                    <Icon className="h-4 w-4 mr-3 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{feature.label}</p>
                      <p className="text-xs text-gray-500 truncate">{feature.description}</p>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Logout Footer */}
            <div className="border-t border-gray-100 mt-1">
              <button
                onClick={() => {
                  logout();
                  setIsProfileOpen(false);
                }}
                className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="h-4 w-4 mr-3 text-red-400" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        )}

        {/* Backdrop for mobile (closes on click) */}
        {isProfileOpen && isMobile && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
            onClick={() => setIsProfileOpen(false)}
          />
        )}
      </div>
    </header>
  );
});

Header.displayName = 'Header';